/**
 * <sticky-product-bar>
 *
 * Shows/hides a fixed bottom bar based on whether the main add-to-cart
 * button is in viewport, and keeps its price/label in sync with the
 * currently selected variant by listening to `variant:change` events
 * dispatched by <product-info>.
 */
class StickyProductBar extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.observer = null;
    this.priceEl = null;
    this.comparePriceEl = null;
    this.discountBadgeEl = null;
    this.priceWrapEl = null;
    this.addToCartButton = null;
    this.iconTemplate = null;
    this.defaultLabel = 'In winkelwagen';
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.comparePriceEl = this.querySelector('[data-sticky-compare-price]');
    this.discountBadgeEl = this.querySelector('[data-sticky-discount-badge]');
    this.priceWrapEl = this.querySelector('[data-sticky-price-wrap]');
    this.imageEl = this.querySelector('[data-sticky-image]');
    this.defaultImageSrc = this.imageEl ? this.imageEl.getAttribute('src') : '';
    this.addToCartButton =
      this.querySelector('[data-sticky-add-to-cart]') ||
      this.querySelector('button[onclick*="product-submit"]');

    if (this.addToCartButton) {
      this.defaultLabel = this.addToCartButton.dataset.stickyLabel || this.defaultLabel;
      const svg = this.addToCartButton.querySelector('svg');
      this.iconTemplate = svg ? svg.cloneNode(true) : null;
      this._renderButton(true);
    }

    this._onStickyClick = this._onStickyClick.bind(this);
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this._onStickyClick);
    }

    this._initObserver();
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('variant:change', this._onVariantChange);
    if (this.addToCartButton && this._onStickyClick) {
      this.addToCartButton.removeEventListener('click', this._onStickyClick);
    }
  }

  _renderButton(isAvailable) {
    if (!this.addToCartButton) return;

    const label = isAvailable ? this.defaultLabel : 'Niet op voorraad';

    this.addToCartButton.replaceChildren();
    if (this.iconTemplate) {
      this.addToCartButton.appendChild(this.iconTemplate.cloneNode(true));
    }
    this.addToCartButton.appendChild(document.createTextNode(` ${label}`));
  }

  _updatePrices(variant, isAvailable, formatMoney) {
    if (!this.priceWrapEl) return;

    if (!isAvailable || !variant) {
      this.priceWrapEl.style.display = 'none';
      return;
    }

    this.priceWrapEl.style.display = '';

    if (this.priceEl) {
      this.priceEl.textContent = formatMoney(variant.price);
    }

    const compareAtPrice = variant.compare_at_price;
    const variantPrice = variant.price;
    const hasSale =
      compareAtPrice != null &&
      variantPrice != null &&
      Number(compareAtPrice) > Number(variantPrice);

    if (this.comparePriceEl) {
      if (hasSale) {
        this.comparePriceEl.hidden = false;
        this.comparePriceEl.textContent = formatMoney(compareAtPrice);
      } else {
        this.comparePriceEl.hidden = true;
        this.comparePriceEl.textContent = '';
      }
    }

    if (this.discountBadgeEl) {
      if (hasSale) {
        const discountPercent = Math.round(100 - (Number(variantPrice) * 100.0) / Number(compareAtPrice));
        this.discountBadgeEl.hidden = false;
        this.discountBadgeEl.textContent = `-${discountPercent}%`;
      } else {
        this.discountBadgeEl.hidden = true;
        this.discountBadgeEl.textContent = '';
      }
    }
  }

  _onStickyClick(event) {
    event.preventDefault();
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    const mainBtn = document.getElementById(`product-submit-${this.sectionId}`);
    if (mainBtn) mainBtn.click();
  }

  _initObserver() {
    const mainAddBtn = document.getElementById(`product-submit-${this.sectionId}`);
    if (!mainAddBtn) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          this.classList.remove('translate-y-full');
        } else {
          this.classList.add('translate-y-full');
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(mainAddBtn);
  }

  _onVariantChange(event) {
    const detail = event.detail || {};
    if (detail.sectionId && detail.sectionId !== this.sectionId) return;

    const variant = detail.variant;
    const isAvailable = !!detail.available;
    const formatMoney = detail.formatMoney || ((amount) => amount);

    this._updatePrices(variant, isAvailable, formatMoney);
    this._renderButton(isAvailable);

    if (this.imageEl && variant) {
      const nextSrc =
        variant.featured_image?.src ||
        variant.featured_media?.preview_image?.src ||
        this.defaultImageSrc;
      if (nextSrc) this.imageEl.src = nextSrc;
    }

    if (this.addToCartButton) {
      if (isAvailable) {
        this.addToCartButton.removeAttribute('disabled');
      } else {
        this.addToCartButton.setAttribute('disabled', 'disabled');
      }
    }
  }
}

if (!customElements.get('sticky-product-bar')) {
  customElements.define('sticky-product-bar', StickyProductBar);
}
